import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useStore } from "./UserContext";
import { faUserSecret, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { stopShadowingUser } from "../../utils/AuthUtils";

/** Minutes left, rounded up, or null when there is no deadline. */
function minutesLeft(expiresAt?: number) {
  if (!expiresAt) return null;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 60000));
}

function Shadowing() {
  const { store, setStore } = useStore();
  const navigate = useNavigate();
  const [remaining, setRemaining] = useState<number | null>(minutesLeft(store?.shadow_expires_at));

  async function stopShadowing() {
    // Awaited so the admin's own record is back in the store before the
    // dashboard renders. Without it the header keeps the shadowed user's name
    // until the page is reloaded by hand.
    await stopShadowingUser(store, setStore);
    navigate("/dashboard");
  }

  // A shadow token lasts minutes. Ending the session on the deadline puts the
  // admin back on their own token, rather than letting the next request 401 and
  // send them to the login screen.
  useEffect(() => {
    if (!store?.impersonatingUser || !store?.shadow_expires_at) return;
    const tick = setInterval(() => {
      const left = minutesLeft(store.shadow_expires_at);
      setRemaining(left);
      if (left === 0) {
        clearInterval(tick);
        // Deliberately not awaited: this is a timer callback, and the session
        // has to end now whether or not the refresh that follows succeeds.
        void stopShadowing();
      }
    }, 15000);
    return () => clearInterval(tick);
  }, [store?.impersonatingUser, store?.shadow_expires_at]);

  if (!store?.impersonatingUser) return null;

  return (
    <div className="flex items-center gap-1.5 rounded-full border border-orange-300 bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
      <FontAwesomeIcon icon={faUserSecret} className="text-orange-500" />
      <span>Shadowing {store.impersonatingUser.name}</span>
      {remaining !== null && <span className="opacity-70">· {remaining}m left</span>}
      <button
        onClick={stopShadowing}
        className="ml-1 opacity-60 hover:opacity-100 transition-opacity"
        title="Stop shadowing"
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
}

export default Shadowing;
